import { Button } from "@/components/button";
import { HeaderPages } from "@/components/header-pages";
import { Input } from "@/components/inputs/input";
import { SearchInput } from "@/components/inputs/search-input";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { CreateBookingParams } from "../../adapters/create-booking/create-booking.types";
import { useBooking } from "../../hooks/use-booking";
import { CreateBookingFormData } from "./booking-create-page.types";
import { CreateBookingSchema } from "./booking-create-page.validation";

export const BookingCreatePage = () => {
  const {
    handleCreateBooking,
    leagues,
    spaces,
    activePrivateRoom,
    handleSelectSpace,
    privateRooms,
  } = useBooking();

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { isValid, isSubmitting },
  } = useForm<CreateBookingFormData>({
    resolver: yupResolver(CreateBookingSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const selectOptionProp = (
    name: keyof CreateBookingParams,
    item: { id: number; name: string },
  ) => {
    clearErrors();

    setValue(name, item);
    if (name === "coworking") {
      handleSelectSpace(item.id);
    }
  };

  return (
    <>
      <HeaderPages title="Criar novo booking"></HeaderPages>
      <div className="max-w-full mx-5 lg:mx-0 lg:max-w-[40%] ">
        <form onSubmit={handleSubmit(handleCreateBooking)}>
          <div className="space-y-12">
            <div className="border-b border-gray-900/10 pb-12">
              <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-full">
                  <Controller
                    control={control}
                    name="company"
                    render={({ field, fieldState }) => (
                      <SearchInput
                        {...field}
                        selectOption={selectOptionProp}
                        searchValue={field.value?.name}
                        optionsList={leagues}
                        label="Company"
                        name="company"
                        value={field.value?.name || ""}
                        type="text"
                        onChange={(event) => {
                          field.onChange({ name: event.target.value });
                        }}
                        required
                        error={fieldState.error ? fieldState.error.message : ""}
                      />
                    )}
                  />
                </div>
                <div className="sm:col-span-full">
                  <Controller
                    control={control}
                    name="coworking"
                    render={({ field, fieldState }) => (
                      <SearchInput
                        {...field}
                        selectOption={selectOptionProp}
                        searchValue={field.value?.name}
                        optionsList={spaces}
                        label="Coworking"
                        name="coworking"
                        value={field.value?.name || ""}
                        type="text"
                        onChange={(event) => {
                          field.onChange({ name: event.target.value });
                        }}
                        required
                        error={fieldState.error ? fieldState.error.message : ""}
                      />
                    )}
                  />
                </div>
                <div className="sm:col-span-full">
                  <Controller
                    control={control}
                    name="private_room"
                    render={({ field, fieldState }) => (
                      <SearchInput
                        {...field}
                        selectOption={selectOptionProp}
                        searchValue={field.value?.name}
                        optionsList={privateRooms}
                        label="Private Room"
                        name="private_room"
                        value={field.value?.name || ""}
                        disable={!activePrivateRoom}
                        type="text"
                        required
                        onChange={(event) => {
                          field.onChange({ name: event.target.value });
                        }}
                        error={fieldState.error ? fieldState.error.message : ""}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="border-b border-gray-900/10 pb-12">
              <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-2 sm:col-start-1">
                  <Controller
                    control={control}
                    name="start_date"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        label="Start date"
                        name="start_date"
                        type="date"
                        value={field.value || ""}
                        onChange={(event) => {
                          field.onChange(event);
                        }}
                        required
                        error={fieldState.error ? fieldState.error.message : ""}
                      />
                    )}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Controller
                    control={control}
                    name="months"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        label="Months"
                        name="months"
                        type="number"
                        value={field.value || ""}
                        onChange={(event) => {
                          field.onChange(event);
                        }}
                        required
                        error={fieldState.error ? fieldState.error.message : ""}
                      />
                    )}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Controller
                    control={control}
                    name="take"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        label="Take %"
                        name="take"
                        type="number"
                        value={field.value || ""}
                        onChange={(event) => {
                          field.onChange(event);
                        }}
                        required
                        error={fieldState.error ? fieldState.error.message : ""}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-x-6">
            <button
              type="button"
              className="text-sm font-semibold leading-6 text-gray-900"
            >
              Cancelar
            </button>
            <Button type="submit" disabled={!isValid && !isSubmitting}>
              Salvar
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};
